import type {
	SearchFieldUmbBlockElementModel,
	SearchFieldUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import {
	SearchField,
	type SearchFieldIntent,
	type SearchFieldSize,
	type SearchFieldVariant,
} from "#/_packages/umbrella/features/search-field/components/search-field";
import { useGetSiteSettings } from "#/_packages/umbrella/hooks/useGetSiteSettings.ts";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import { toHoverItemCssClasses } from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils";
import { tryGetAnimationScrollType } from "#/_packages/umbrella/utils";

type SearchFieldBlockProps = BlockItemProps & {
	content: SearchFieldUmbBlockElementModel;
	settings: SearchFieldUmbBlockSettingsElementModel;
	isHeaderDrawer?: boolean;
	isPreview?: boolean;
};

export default function SearchFieldUmbBlock({
	className,
	content,
	settings,
	isHeaderDrawer = false,
	isPreview = false,
}: SearchFieldBlockProps) {
	const props = content.properties;
	const settingsProps = settings.properties;
	const animationScrollType = tryGetAnimationScrollType(
		settingsProps?.animationScrollType,
	);
	const siteSettings = useGetSiteSettings();
	const customSearchEngine = ["true", "1"].includes(
		props?.customSearchEngine?.toLowerCase() ?? "",
	);
	const searchPageUrl = siteSettings?.searchPage?.route.path;
	if (
		!isPreview &&
		!searchPageUrl &&
		!(customSearchEngine && props?.customUrl?.includes("%s"))
	) {
		return null;
	}
	// Match Razor's Bootstrap breakpoints, including its desktop-only default.
	const visibility =
		isHeaderDrawer || isPreview
			? []
			: settingsProps?.responsiveVisibility?.length
				? settingsProps.responsiveVisibility
				: ["xl"];

	return (
		<SearchField
			textLabel={props?.textLabel ?? undefined}
			body={
				props?.body?.markup ? (
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Umbraco rich text is already rendered HTML from the CMS.
					<div
						className="rich-text"
						dangerouslySetInnerHTML={{ __html: props.body.markup }}
					/>
				) : undefined
			}
			size={(props?.size as SearchFieldSize) ?? undefined}
			intentColor={(props?.intentColor as SearchFieldIntent) ?? undefined}
			typeVariant={(props?.typeVariant as SearchFieldVariant) ?? undefined}
			customSearchEngine={customSearchEngine}
			customUrl={props?.customUrl ?? undefined}
			customTargetBlank={props?.customTarget ?? undefined}
			searchPageUrl={searchPageUrl ?? ""}
			className={[
				!isHeaderDrawer && !isPreview && "search-field-responsive",
				...visibility.map((breakpoint) => `search-field-show-${breakpoint}`),
				toHoverItemCssClasses(settingsProps?.animationHoverType),
				animationScrollType !== "none" ? animationScrollType : undefined,
				className,
			]
				.filter(Boolean)
				.join(" ")}
		/>
	);
}
